import React, { FormEvent, useState } from 'react'
import { useForm, Link, router } from '@inertiajs/react'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Chip from '@mui/material/Chip'
import { SidebarLayout } from '@/components/layouts/SidebarLayout'

interface SecretData {
  id: number
  name: string
  description: string | null
}

interface AssignedAgent {
  id: number
  agent_id: number
  username: string
  agent_type: string
  slug: string
}

interface AgentOption {
  id: number
  username: string
  agent_type: string
}

interface GrantRequest {
  id: number
  agent_username: string
  reason: string
  created_at: string
}

interface Props {
  secret: SecretData
  agents: AssignedAgent[]
  all_agents: AgentOption[]
  grant_requests: GrantRequest[]
}

export default function Edit({ secret, agents, all_agents, grant_requests }: Props) {
  const [selectedAgentId, setSelectedAgentId] = useState<number | ''>('')

  const { data, setData, put, processing, errors } = useForm({
    secret: {
      name: secret.name,
      description: secret.description || '',
      secret: '',
    },
  })

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    put(`/manage/secrets/${secret.id}`)
  }

  function handleDelete() {
    if (confirm(`Are you sure you want to delete secret '${secret.name}'? This cannot be undone.`)) {
      router.delete(`/manage/secrets/${secret.id}`)
    }
  }

  function handleAddAgent() {
    if (selectedAgentId) {
      router.post(`/manage/secrets/${secret.id}/add_agent`, { agent_id: selectedAgentId })
      setSelectedAgentId('')
    }
  }

  function handleRemoveAgent(agentSecret: AssignedAgent) {
    if (confirm(`Remove access for '${agentSecret.username}'?`)) {
      router.delete(`/manage/secrets/${secret.id}/remove_agent/${agentSecret.id}`)
    }
  }

  function handleApprove(requestId: number) {
    router.post(`/manage/secrets/grant_requests/${requestId}/approve`)
  }

  function handleDeny(requestId: number) {
    router.post(`/manage/secrets/grant_requests/${requestId}/deny`)
  }

  return (
    <SidebarLayout section="manage" title={`Edit secret: ${secret.name}`}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 5 },
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          maxWidth: 800,
        }}
      >
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            label="Name"
            fullWidth
            autoFocus
            value={data.secret.name}
            onChange={e => setData('secret', { ...data.secret, name: e.target.value })}
            error={'secret.name' in errors}
            helperText={(errors as Record<string, string>)['secret.name']}
            sx={{ mb: 2.5 }}
          />
          <TextField
            label="Description"
            fullWidth
            multiline
            minRows={2}
            value={data.secret.description}
            onChange={e => setData('secret', { ...data.secret, description: e.target.value })}
            error={'secret.description' in errors}
            helperText={(errors as Record<string, string>)['secret.description']}
            sx={{ mb: 2.5 }}
          />
          <TextField
            label="Secret value"
            fullWidth
            multiline
            minRows={4}
            value={data.secret.secret}
            onChange={e => setData('secret', { ...data.secret, secret: e.target.value })}
            error={'secret.secret' in errors}
            helperText={(errors as Record<string, string>)['secret.secret'] || 'Leave blank to keep current value'}
            sx={{ mb: 3 }}
          />
          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={processing}
            sx={{ mb: 2 }}
          >
            Update secret
          </Button>
          <Box sx={{ textAlign: 'center' }}>
            <Link href="/manage/secrets" style={{ color: '#1a1a1a' }}>Back to secrets</Link>
          </Box>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h6" sx={{ mb: 2 }}>Agent access</Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 200, flexGrow: 1 }}>
            <InputLabel>Add agent</InputLabel>
            <Select
              value={selectedAgentId}
              label="Add agent"
              onChange={e => setSelectedAgentId(e.target.value as number)}
            >
              {all_agents.map(a => (
                <MenuItem key={a.id} value={a.id}>{a.username} ({a.agent_type})</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" onClick={handleAddAgent} disabled={!selectedAgentId}>
            Add
          </Button>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Agent</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {agents.map((agent) => (
                <TableRow key={agent.id}>
                  <TableCell>{agent.username}</TableCell>
                  <TableCell>
                    <Chip label={agent.agent_type} size="small" variant="outlined" color={agent.agent_type === 'machine' ? 'secondary' : 'default'} />
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" color="error" onClick={() => handleRemoveAgent(agent)}>
                      Remove
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {agents.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No agents have access to this secret.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {grant_requests.length > 0 && (
          <>
            <Divider sx={{ my: 4 }} />
            <Typography variant="h6" sx={{ mb: 2 }}>Pending grant requests</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Agent</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {grant_requests.map((gr) => (
                    <TableRow key={gr.id}>
                      <TableCell>{gr.agent_username}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {gr.reason}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {new Date(gr.created_at).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Button size="small" color="success" onClick={() => handleApprove(gr.id)} sx={{ mr: 1 }}>
                          Approve
                        </Button>
                        <Button size="small" color="error" onClick={() => handleDeny(gr.id)}>
                          Deny
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        <Divider sx={{ my: 3 }} />
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Danger zone
          </Typography>
          <Button
            variant="outlined"
            color="error"
            fullWidth
            onClick={handleDelete}
          >
            Delete this secret
          </Button>
        </Box>
      </Paper>
    </SidebarLayout>
  )
}
